// subparts/TimetableEntryCard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from 'primereact/toast';
import { GlobalChipSelector, ChipOption } from '../../../../components/GlobalComponents/GlobalChipSelector';
import { GlobalCheckboxes, CheckboxOption } from '../../../../components/GlobalComponents/GlobalCheckboxes';
import { GlobalInputField } from '../../../../components/GlobalComponents/GlobalInputField';
import apiClient from '../../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasOptionalTeacher, formatSlotTypeLabel } from './timetableSlotTypes';

interface TimetableEntry {
  id: string;
  timeSlotId: string;
  subjectId: string;
  // Holds the real teacher_id (name kept for compatibility with the parent form state).
  teacherAssignmentId: string;
  daysOfWeek: string[];
  roomNumber: string;
}

interface TimetableEntryCardProps {
  entry: TimetableEntry;
  index: number;
  classId: string;
  section: string | null;
  canRemove: boolean;
  timeSlotOptions: ChipOption[];
  subjectOptions: ChipOption[];
  timeSlotsLoading: boolean;
  subjectsLoading: boolean;
  timeSlotsError?: string | null;
  subjectsError?: string | null;
  canShowEntries: boolean;
  loading: boolean;
  occupiedDays?: string[];
  slotType?: string | null;
  /**
   * subject_id → teacher already teaching that subject for this class + section
   * in the selected timetable type. The backend allows only ONE teacher per
   * class + section + subject, so when a subject is in this map the teacher is
   * locked to that person.
   */
  subjectTeacherMap?: Record<string, { id: string; name: string }>;
  onUpdate: (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => void;
  onUpdateDays: (id: string, days: string[]) => void;
  onRemove: (id: string) => void;
}

interface TeacherDetails {
  profile_picture_url?: string;
  employee_id?: string;
  sp_teacher_id?: string;
  contact_no?: string;
  email_id?: string;
  gender?: string;
  qualification?: string;
  joining_date?: string;
  address?: string;
  status?: string;
}

// value === the teacher's id, so the chip value and the API payload are the same thing.
interface TeacherOption extends ChipOption {
  teacherId: string;
  details?: TeacherDetails;
}

const SELECT_ALL_VALUE = '__SELECT_ALL__';
const TEACHER_PAGE_SIZE = 100; // backend caps `limit` at 100
const TEACHER_MAX_PAGES = 20;  // safety stop: 2000 teachers

const getId = (item: any): string => String(item.id || item._id || item.ID || '');

const sortByLabel = (list: TeacherOption[]) => [...list].sort((a, b) => a.label.localeCompare(b.label));

const toDetails = (t: any): TeacherDetails => ({
  profile_picture_url: t.profile_picture_url,
  employee_id: t.employee_id,
  sp_teacher_id: t.sp_teacher_id,
  contact_no: t.contact_no,
  email_id: t.email_id,
  gender: t.gender,
  qualification: t.qualification,
  joining_date: t.joining_date,
  address: t.address,
  status: t.status,
});

// The API stores "unknown" for fields nobody filled in — treat that as empty.
const clean = (v?: string): string => {
  const t = (v ?? '').trim();
  return t && t.toLowerCase() !== 'unknown' ? t : '';
};

const formatDate = (v?: string): string => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ══════════════════════════════════════════════════════════════════════
   Teacher chips: photo + name + an eye icon that shows the teacher's
   details on hover, so two teachers with the same name can be told apart.
══════════════════════════════════════════════════════════════════════ */

const TeacherAvatar: React.FC<{ url?: string; name: string; size: number; selected?: boolean; rounded?: boolean }> = ({
  url,
  name,
  size,
  selected,
  rounded = false,
}) => {
  const [failed, setFailed] = useState(false);
  const initials =
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?';

  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          width: size,
          height: size,
          borderRadius: rounded ? '18px' : '50%',
          objectFit: 'cover',
          objectPosition: 'center 20%', // keep the face in frame when the photo is a portrait
          flexShrink: 0,
          background: '#f1f5f9',
          border: selected ? '1.5px solid rgba(255,255,255,0.85)' : '1.5px solid #e5e7eb',
        }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: rounded ? '18px' : '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: Math.round(size * 0.42),
        fontWeight: 700,
        background: selected ? 'rgba(255,255,255,0.25)' : '#e0e7ff',
        color: selected ? 'white' : '#3730a3',
      }}
    >
      {initials}
    </span>
  );
};

const DETAILS_CARD_WIDTH = 300;

const TeacherDetailsCard: React.FC<{ option: TeacherOption; anchor: DOMRect }> = ({ option, anchor }) => {
  const d = option.details;

  // open to the right of the eye icon; flip to the left when there is no room
  const left =
    anchor.right + 10 + DETAILS_CARD_WIDTH > window.innerWidth
      ? Math.max(8, anchor.left - 10 - DETAILS_CARD_WIDTH)
      : anchor.right + 10;
  const top = Math.max(8, Math.min(anchor.top - 12, window.innerHeight - 450));

  const rows: Array<[string, string]> = (
    [
      ['Employee ID', clean(d?.employee_id)],
      ['Teacher ID', clean(d?.sp_teacher_id)],
      ['Phone', clean(d?.contact_no)],
      ['Email', clean(d?.email_id)],
      ['Gender', clean(d?.gender)],
      ['Qualification', clean(d?.qualification)],
      ['Joined', formatDate(clean(d?.joining_date))],
      ['Address', clean(d?.address)],
    ] as Array<[string, string]>
  ).filter(([, v]) => !!v);

  const active = (d?.status ?? '').toLowerCase() === 'active';

  return createPortal(
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left,
        top,
        width: DETAILS_CARD_WIDTH,
        zIndex: 5000, // above PrimeReact dialogs
        pointerEvents: 'none',
        background: 'white',
        border: '1px solid #dbeafe',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 16px 40px rgba(30, 64, 175, 0.22)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: '10px',
          marginBottom: rows.length ? '14px' : 0,
        }}
      >
        <TeacherAvatar url={d?.profile_picture_url} name={option.label} size={132} rounded />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', lineHeight: 1.25 }}>{option.label}</div>
          {d?.status && (
            <span
              style={{
                display: 'inline-block',
                marginTop: '6px',
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 10px',
                borderRadius: '10px',
                textTransform: 'capitalize',
                background: active ? 'rgba(16,185,129,0.12)' : 'rgba(100,116,139,0.12)',
                color: active ? '#047857' : '#475569',
              }}
            >
              {d.status}
            </span>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: '12.5px', color: '#64748b' }}>No more details on file.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', rowGap: '6px', columnGap: '10px' }}>
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{k}</div>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#1f2937', wordBreak: 'break-word' }}>{v}</div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};

interface TeacherChipsProps {
  label: string;
  options: TeacherOption[];
  value: string;
  onChange: (teacherId: string) => void;
  required: boolean;
  loading: boolean;
  disabled: boolean;
  allowDeselect: boolean;
  emptyMessage: string;
}

const TeacherChips: React.FC<TeacherChipsProps> = ({
  label,
  options,
  value,
  onChange,
  required,
  loading,
  disabled,
  allowDeselect,
  emptyMessage,
}) => {
  const [hover, setHover] = useState<{ id: string; rect: DOMRect } | null>(null);
  const hoverOption = hover ? options.find((o) => o.value === hover.id) : undefined;

  const show = (id: string, el: HTMLElement) => setHover({ id, rect: el.getBoundingClientRect() });
  const hide = () => setHover(null);

  const handleSelect = (id: string) => {
    if (value === id) {
      if (allowDeselect) onChange('');
      return;
    }
    onChange(id);
  };

  return (
    <div>
      <style>{`
        .tec-row { display: flex; flex-wrap: wrap; gap: 8px; }
        .tec-chip {
          display: inline-flex;
          align-items: center;
          border: 1.5px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          transition: all 0.2s ease;
        }
        .tec-chip:hover:not(.selected):not(.disabled) { background: #f0f9ff; }
        .tec-chip.selected {
          border-color: #3b82f6;
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
        }
        .tec-chip.disabled:not(.selected) { opacity: 0.55; }
        .tec-chip-main {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 5px 6px 5px 6px;
          border: none;
          background: transparent;
          border-radius: 8px 0 0 8px;
          font-size: 12.5px;
          font-weight: 600;
          color: #1f2937;
          cursor: pointer;
          outline: none;
        }
        .tec-chip-main:disabled { cursor: default; }
        .tec-chip.selected .tec-chip-main { color: white; }
        .tec-chip-eye {
          align-self: stretch;
          display: inline-flex;
          align-items: center;
          padding: 0 9px 0 5px;
          border: none;
          background: transparent;
          border-radius: 0 8px 8px 0;
          color: #64748b;
          cursor: help;
          outline: none;
        }
        .tec-chip-eye:hover, .tec-chip-eye:focus-visible { color: #1e40af; }
        .tec-chip.selected .tec-chip-eye { color: rgba(255, 255, 255, 0.85); }
        .tec-chip.selected .tec-chip-eye:hover,
        .tec-chip.selected .tec-chip-eye:focus-visible { color: white; }
        .tec-chip-main:focus-visible, .tec-chip-eye:focus-visible { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.35); }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '11.5px',
          fontWeight: 700,
          color: '#1e40af',
          letterSpacing: '0.02em',
          marginBottom: '10px',
        }}
      >
        <i className="pi pi-user" style={{ fontSize: '12px' }} />
        {label}
        {required && <span style={{ color: '#ef4444' }}>*</span>}
      </div>

      {loading ? (
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Loading teachers…</div>
      ) : options.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>{emptyMessage}</div>
      ) : (
        <div className="tec-row">
          {options.map((opt) => {
            const selected = value === opt.value;
            return (
              <div
                key={opt.value}
                className={`tec-chip${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
              >
                <button
                  type="button"
                  className="tec-chip-main"
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() => handleSelect(opt.value)}
                >
                  <TeacherAvatar url={opt.details?.profile_picture_url} name={opt.label} size={24} selected={selected} />
                  {opt.label}
                </button>
                <button
                  type="button"
                  className="tec-chip-eye"
                  aria-label={`View details of ${opt.label}`}
                  onMouseEnter={(e) => show(opt.value, e.currentTarget)}
                  onMouseLeave={hide}
                  onFocus={(e) => show(opt.value, e.currentTarget)}
                  onBlur={hide}
                  onClick={(e) => {
                    // for touch screens, where there is no hover
                    e.stopPropagation();
                    show(opt.value, e.currentTarget);
                  }}
                >
                  <i className="pi pi-eye" style={{ fontSize: '13px' }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {hover && hoverOption && <TeacherDetailsCard option={hoverOption} anchor={hover.rect} />}
    </div>
  );
};

const TimetableEntryCard: React.FC<TimetableEntryCardProps> = ({
  entry,
  index,
  classId,
  canRemove,
  timeSlotOptions,
  subjectOptions,
  timeSlotsLoading,
  subjectsLoading,
  timeSlotsError = null,
  subjectsError = null,
  canShowEntries,
  loading,
  occupiedDays = [],
  slotType = null,
  subjectTeacherMap = {},
  onUpdate,
  onUpdateDays,
  onRemove,
}) => {
  const toast = useRef<Toast>(null);

  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  const noSubject = hasNoSubject(slotType);
  const optionalTeacher = hasOptionalTeacher(slotType);
  // Strict non-teaching: no subject AND no teacher allowed (i.e. 'break')
  const noTeacherAtAll = noSubject && !optionalTeacher;

  // No time slots exist at all for this timetable type — the rest of the
  // entry (subject/teacher/days/room) is meaningless without one, so lock it down.
  const noTimeSlotsAvailable = canShowEntries && !timeSlotsLoading && timeSlotOptions.length === 0;

  const baseDayOptions: CheckboxOption[] = [
    { value: 'Monday',    label: 'Monday',    disabled: occupiedDays.includes('Monday')    },
    { value: 'Tuesday',   label: 'Tuesday',   disabled: occupiedDays.includes('Tuesday')   },
    { value: 'Wednesday', label: 'Wednesday', disabled: occupiedDays.includes('Wednesday') },
    { value: 'Thursday',  label: 'Thursday',  disabled: occupiedDays.includes('Thursday')  },
    { value: 'Friday',    label: 'Friday',    disabled: occupiedDays.includes('Friday')    },
    { value: 'Saturday',  label: 'Saturday',  disabled: occupiedDays.includes('Saturday')  },
  ];

  const selectableDayValues = baseDayOptions.filter((d) => !d.disabled).map((d) => d.value);
  const allDaysSelected =
    selectableDayValues.length > 0 &&
    selectableDayValues.every((d) => entry.daysOfWeek.includes(d));

  // "Select All" rendered as the 7th option in the SAME checkbox list, right after Saturday.
  const dayOptions: CheckboxOption[] = [
    ...baseDayOptions,
    {
      value: SELECT_ALL_VALUE,
      label: 'Select All',
      disabled: loading || noTimeSlotsAvailable || selectableDayValues.length === 0,
    },
  ];

  const checkboxValue = allDaysSelected ? [...entry.daysOfWeek, SELECT_ALL_VALUE] : entry.daysOfWeek;

  const handleDaysChange = (values: string[]) => {
    const toggledSelectAll = values.includes(SELECT_ALL_VALUE) && !allDaysSelected;
    const toggledOffSelectAll = !values.includes(SELECT_ALL_VALUE) && allDaysSelected;

    if (toggledSelectAll) {
      onUpdateDays(entry.id, selectableDayValues);
      return;
    }
    if (toggledOffSelectAll) {
      onUpdateDays(entry.id, []);
      return;
    }
    onUpdateDays(entry.id, values.filter((v) => v !== SELECT_ALL_VALUE));
  };

  /* ══════════════════════════════════════════════════════════════════
     TEACHER LIST
     Assignments no longer exist before the timetable (they are generated from
     it on Sync), so the teacher list has nothing to do with assignments.
     Every slot type uses the plain school teacher list:
       GET teachers  (page + limit)
     Pages are walked until one comes back short or adds nobody new, so schools
     with more than 100 teachers are not cut off.
  ══════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (noTimeSlotsAvailable || noTeacherAtAll) {
      setTeacherOptions([]);
      return;
    }

    let cancelled = false;

    const loadSchoolTeachers = async () => {
      const collected = new Map<string, TeacherOption>();

      for (let page = 1; page <= TEACHER_MAX_PAGES; page += 1) {
        console.log('[TimetableEntryCard] fetching teachers', {
          endpoint: 'teachers',
          params: { page, limit: TEACHER_PAGE_SIZE },
          entryId: entry.id,
          slotType,
        });

        const response = await apiClient.get('teachers', {
          params: { page, limit: TEACHER_PAGE_SIZE },
        });

        console.log('[TimetableEntryCard] teachers response', {
          page,
          status: response.data.status,
          rowCount: Array.isArray(response.data.data) ? response.data.data.length : null,
          raw: response.data,
        });

        if (response.data.status !== 'success') {
          console.warn('[TimetableEntryCard] teachers did not return success, stopping', response.data);
          break;
        }

        const rows: any[] = response.data.data ?? [];
        const before = collected.size;
        rows.forEach((t) => {
          const id = getId(t);
          if (id) collected.set(id, { value: id, label: t.name || '', teacherId: id, details: toDetails(t) });
        });

        // short page = last page; no new teachers = the endpoint ignored `page`
        if (rows.length < TEACHER_PAGE_SIZE || collected.size === before) {
          console.log('[TimetableEntryCard] stop paging', {
            page,
            rowsInPage: rows.length,
            newTeachers: collected.size - before,
          });
          break;
        }
      }

      const teachers = Array.from(collected.values());
      console.log('[TimetableEntryCard] teachers loaded', { total: teachers.length, teachers });
      return teachers;
    };

    (async () => {
      setTeachersLoading(true);
      try {
        const list = await loadSchoolTeachers();
        if (!cancelled) setTeacherOptions(sortByLabel(list));
      } catch (error) {
        console.error('[TimetableEntryCard] error fetching teachers:', error);
        if (!cancelled) {
          setTeacherOptions([]);
          toast.current?.show({
            severity: 'error',
            summary: 'Teachers',
            detail: 'Could not load the teachers list.',
            life: 4000,
          });
        }
      } finally {
        if (!cancelled) setTeachersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [noTimeSlotsAvailable, noTeacherAtAll]);

  /* ── One teacher per class + section + subject ────────────────────
     If this subject is already scheduled for this class + section in the
     selected timetable type, the teacher is that person — pre-select and lock. */
  const lockedTeacher =
    !optionalTeacher && !noSubject && entry.subjectId ? subjectTeacherMap[entry.subjectId] : undefined;

  useEffect(() => {
    if (lockedTeacher && entry.teacherAssignmentId !== lockedTeacher.id) {
      onUpdate(entry.id, 'teacherAssignmentId', lockedTeacher.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedTeacher?.id, entry.subjectId]);

  const visibleTeacherOptions: TeacherOption[] = lockedTeacher
    ? [
        teacherOptions.find((t) => t.value === lockedTeacher.id) ?? {
          value: lockedTeacher.id,
          label: lockedTeacher.name,
          teacherId: lockedTeacher.id,
        },
      ]
    : teacherOptions;

  const showTeacherSelector = !noTimeSlotsAvailable && (optionalTeacher || (!noSubject && !!entry.subjectId));

  /* ── Empty-state messages, derived from real state — not guessed ── */

  const timeSlotEmptyMessage = !canShowEntries
    ? 'Complete previous fields first'
    : timeSlotsError
      ? timeSlotsError
      : 'No time slots configured for this timetable type';

  const subjectEmptyMessage = !classId
    ? 'Select class first'
    : subjectsError
      ? subjectsError
      : 'No subjects available for this class';

  const teacherEmptyMessage = !optionalTeacher && !entry.subjectId
    ? 'Select subject first'
    : 'No teachers found in this school';

  return (
    <>
    <Toast ref={toast} position="top-right" />

    <div className="entry-card">
      <div className="entry-number">Entry {index + 1}</div>

      {canRemove && (
        <button
          className="remove-button"
          onClick={() => onRemove(entry.id)}
          disabled={loading}
        >
          <i className="pi pi-times" style={{ fontSize: '11px' }} />
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
        <GlobalChipSelector
          label="Time Slot"
          icon="pi pi-clock"
          value={entry.timeSlotId}
          onChange={(v) => onUpdate(entry.id, 'timeSlotId', v)}
          options={timeSlotOptions}
          required
          loading={timeSlotsLoading}
          disabled={loading || !canShowEntries}
          emptyMessage={timeSlotEmptyMessage}
        />

        {noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            No time slots are configured for this timetable type — add one before filling out the rest of this entry
          </div>
        )}

        {!noSubject && (
          <GlobalChipSelector
            label="Subject"
            icon="pi pi-book"
            value={entry.subjectId}
            onChange={(v) => {
              onUpdate(entry.id, 'subjectId', v);
              // Clear teacher when subject changes — the lock effect above re-selects
              // the existing teacher if this subject is already scheduled.
              onUpdate(entry.id, 'teacherAssignmentId', '');
            }}
            options={subjectOptions}
            required
            loading={subjectsLoading}
            disabled={loading || !classId || noTimeSlotsAvailable}
            emptyMessage={subjectEmptyMessage}
          />
        )}

        {noTeacherAtAll && !noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            This is a {formatSlotTypeLabel(slotType || '')} slot — no subject or teacher needed
          </div>
        )}

        {showTeacherSelector && (
          <TeacherChips
            label={optionalTeacher ? 'Supervising Teacher (optional)' : 'Teacher'}
            value={entry.teacherAssignmentId}
            onChange={(v) => onUpdate(entry.id, 'teacherAssignmentId', v)}
            options={visibleTeacherOptions}
            required={!optionalTeacher}
            loading={teachersLoading}
            disabled={loading || !!lockedTeacher}
            allowDeselect={!lockedTeacher}
            emptyMessage={teacherEmptyMessage}
          />
        )}

        {lockedTeacher && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            {lockedTeacher.name} already teaches this subject for this class and section in this timetable.
            Only one teacher can teach a subject per class and section.
          </div>
        )}

        <div className="days-checkbox-row">
          <style>{`
            .days-checkbox-row label:has(input[value="${SELECT_ALL_VALUE}"]),
            .days-checkbox-row div:has(> input[value="${SELECT_ALL_VALUE}"]) {
              background: #eff6ff;
              border-radius: 6px;
              padding: 4px 8px;
            }
          `}</style>
          <GlobalCheckboxes
            label="Days of Week"
            options={dayOptions}
            value={checkboxValue}
            onChange={handleDaysChange}
            required
            layout="horizontal"
            gap={16}
            disabled={loading || noTimeSlotsAvailable}
          />
        </div>

        <GlobalInputField
          label="Room Number"
          value={entry.roomNumber}
          onChange={(e) => onUpdate(entry.id, 'roomNumber', e.target.value)}
          placeholder="e.g. 101, Lab-3, Auditorium"
          disabled={loading || noTimeSlotsAvailable}
          icon="pi pi-home"
        />
      </div>
    </div>
    </>
  );
};

export default TimetableEntryCard;